import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Grid, LinearProgress, DialogTitle, DialogContent, Dialog, Box, CircularProgress,
  Accordion, AccordionDetails, AccordionSummary, DialogActions, Button, Tooltip, IconButton,
  Stack, Typography, FormControlLabel, Checkbox, Divider, Card, CardContent, Collapse,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SyncIcon from '@mui/icons-material/Sync'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoIcon from '@mui/icons-material/Info'
import { useSelector } from 'react-redux'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from "react-draggable"
import { toast } from 'react-toastify'
import stylesheet from './questionnaire.stylesheet'
import QuestionnaireComponent from './questionnaire.component'
import { RootState } from '../../redux/store'
import CardLayoutComponent from '../patients/detail/common/card-layout.component'
import DialogTitleHeaderComponent from '../patients/detail/common/dialog-title-header.component'
import Helper from '../../utils/helper'
import { PatientService } from '../../services/patient.service'
import { useUpdateDataAction } from '../../utils/update-details-hook'
import { formatDisplayName, hasDifferentPrefix } from '../../utils/display-name-formatter'
import QuickAnswerButtons from './QuickAnswerButtons'
import BatchChipGroup from './BatchChipGroup'
import { buildBatchSavePayload } from './category-quesionnaries.batchsave'
import { selectGroupsToDeny } from './category-quesionnaries.denyremaining'
import { chipConfirmDefault, chipDenyValue, computeBranchedRefresh } from './search-quesionnaries.chip-value'

interface IProps {
  open: boolean
  title: string
  isBulkEdit?:boolean
  isSearch?: boolean
  item: any
  input?: any
  handleClose: (data: any) => void
  onDataSaved?: (updatedData?: any[]) => void
  classes: ClassNameMap
}

interface QuestionnaireItem {
  id: string
  questionnaire_id: string
  confirmed: boolean
  answer: any
}

const CategoryQuestionnaireComponent: React.FC<IProps> = ({
  open,
  title,
  isBulkEdit,
  isSearch,
  item,
  input,
  handleClose,
  onDataSaved,
  classes
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isBulkSaving, setIsBulkSaving] = useState(false)
  // Initialize data from item immediately in search mode to avoid a blank-then-flash first render
  const [data, setData] = useState(isSearch ? item : []) as any
  const [isSubmit, setIsSubmit] = useState(false)
  const [isExpandQ, setIsExpandQ] = useState(() => isSearch && !input?.dependent_questionnaires && item?.length === 1)
  const [isCurrQuestionLoading, setIsCurrQuestionLoading] = useState('')
  // Auto-open accordion in search mode with a single item, same as the useEffect would do
  const [openAccordion, setOpenAccordion] = useState<Set<string>>(() => {
    if (isSearch && !input?.dependent_questionnaires && item?.length === 1) {
      return new Set(item.map((i: any) => i.id))
    }
    return new Set()
  })
  // Pre-populate queObj in search mode so action buttons are immediately enabled
  const [queObj, setQueObj] = useState<Map<string, any>>(() => {
    if (isSearch && Array.isArray(item) && item.length > 0) {
      const map = new Map<string, QuestionnaireItem>()
      item.forEach((dataItem: any) => {
        map.set(dataItem.id, {
          id: dataItem.id,
          questionnaire_id: dataItem.questionnaire_id,
          confirmed: true,
          answer: {}
        })
      })
      return map
    }
    return new Map()
  })

  // Questionnaire options stored in a ref to avoid triggering renderGroup recomputation.
  // Values are read from the ref whenever renderGroup recomputes for other reasons.
  const questionnaireOptionsRef = useRef<Map<string, string[]>>(new Map())

  // Batch group data from API
  const [batchGroupData, setBatchGroupData] = useState<{ mapping: Record<string, any>, groups: Record<string, any> }>({ mapping: {}, groups: {} })

  // Inline editing for batch chip items
  const [editingBatchItem, setEditingBatchItem] = useState<any>(null)

  // QuickEdit overrides — stored in a ref so handleChange (from QuestionnaireComponent init) can't overwrite them
  const quickEditOverrides = useRef<Map<string, any>>(new Map())

  // User-edited answers for inline batch editor — separate from chip-toggle defaults in quickEditOverrides.
  // Used as overrideAnswer so values survive chip navigation (unmount/remount).
  const batchEditAnswersRef = useRef<Map<string, any>>(new Map())

  // Ref for data access inside callbacks to avoid dependency on data state
  const dataRef = useRef<any[]>([])
  dataRef.current = data

  // Ref to read queObj inside effects without adding it as a dependency
  const queObjRef = useRef<Map<string, any>>(new Map())
  queObjRef.current = queObj

  // Tracks items where the user has actually made unsaved local changes.
  // queObj is populated on accordion open (initAnswer=true) so it can't be used
  // as a sync guard — it blocks syncs even when no real edit has been made.
  const pendingLocalEditsRef = useRef<Set<string>>(new Set())

  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)

  // Refs
  const queRefs = useRef<any[]>([])
  const calRef = useRef(null)

  // Selectors
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const selectedProgram = useSelector((state: RootState) => state.commonData.selectedProgram)
  const recommended_calculators = useSelector((state: RootState) => state.commonData.recommended_calculators)

  // Services
  const patientService = useMemo(() => new PatientService(), [])
  const { updatePatientData } = useUpdateDataAction(patientDetail?.id)

  // Convert selectedProgram to Set for O(1) lookup
  const selectedProgramSet = useMemo(() => new Set(selectedProgram), [selectedProgram])

  // Fetch batch groups from API on mount
  useEffect(() => {
    patientService.getBatchGroups().then(res => {
      if (res?.data?.mapping) {
        setBatchGroupData(res.data)
      }
    }).catch(() => {})
  }, [patientService])

  // Initial loading effect
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000)
    return () => clearTimeout(timer)
  }, [])

  // Update the initial loading effect
  useEffect(() => {
    setIsLoading(true)
    questionnaireOptionsRef.current = new Map() // Clear options on dialog open
    quickEditOverrides.current.clear()
    batchEditAnswersRef.current.clear()
  }, [open])

  const getQuestionId = (qId: any) => {
    const calculators = [
      ...patientDetail?.measurements?.calculators || [],
      ...patientDetail?.measurements?.recommended_calculators || []
    ] as any
    return calculators?.find((i: any) => i.questionnaire_id === qId)
  }

  useEffect(() => {
    if (input?.dependent_questionnaires) {
      const updatedData = item?.map((i: any) => {
        const currObj = getQuestionId(i.questionnaire_id)
        return {
          ...i,
          id: currObj?.id || i.id || i.questionnaire_id,
          needs_confirmation: currObj?.needs_confirmation,
          first_component_type: currObj?.first_component_type || i.first_component_type,
          first_response: currObj?.first_response ?? i.first_response
        }
      })
      setData(updatedData)
    } else
      setData(item)
  }, [item])

  // When patientDetail refreshes (e.g. after a single questionnaire save), sync first_response
  // for items that have no pending local changes so the chip selection stays accurate.
  useEffect(() => {
    if (!patientDetail?.measurements) return
    const calculators = [
      ...patientDetail.measurements.calculators || [],
      ...patientDetail.measurements.recommended_calculators || []
    ] as any[]
    setData((prev: any) => {
      if (!prev?.length) return prev
      let changed = false
      const updated = prev.map((d: any) => {
        if (pendingLocalEditsRef.current.has(String(d.id))) return d  // skip items with actual unsaved user edits
        const currObj = calculators.find((c: any) => c.id === d.id)
        if (!currObj || currObj.first_response === d.first_response) return d
        changed = true
        return { ...d, first_response: currObj.first_response }
      })
      return changed ? updated : prev
    })
  }, [patientDetail])

  useEffect(() => {
    if (isExpandQ) {
      const accordionIds = data.map((dataItem: any) => dataItem.id)
      setOpenAccordion(new Set(accordionIds))
    } else
      setOpenAccordion(new Set())
  }, [isExpandQ])

  // Handle data updates
  useEffect(() => {
    if (isSubmit) {
      setIsSubmit(false)
      setIsLoading(false)
      setOpenAccordion(new Set())
    }

    if (isSearch && !input?.dependent_questionnaires && data?.length === 1) {
      const accordionIds = data.map((dataItem: any) => dataItem.id)
      setOpenAccordion(new Set(accordionIds))
      setIsExpandQ(true)
    }

    // Sync queObj with any new items in data, but preserve answers that have already been edited.
    // Using a functional update so we can read prev queObj without adding it as a dep.
    if (isSearch && data?.length > 0) {
      setQueObj(prev => {
        let changed = false
        const newMap = new Map(prev)
        data.forEach((dataItem: any) => {
          const existing = prev.get(dataItem.id)
          if (!existing) {
            newMap.set(dataItem.id, {
              id: dataItem.id,
              questionnaire_id: dataItem.questionnaire_id,
              confirmed: true,
              answer: {}
            })
            changed = true
          } else if (existing.questionnaire_id !== dataItem.questionnaire_id) {
            // Update metadata but keep the answer
            newMap.set(dataItem.id, { ...existing, questionnaire_id: dataItem.questionnaire_id })
            changed = true
          }
        })
        return changed ? newMap : prev
      })
    }
  }, [data, isSubmit, isSearch, input?.dependent_questionnaires])

  // Memoized helper functions
  const getQuestionnaireName = useCallback(
    (id: string) => formatDisplayName(data.find((i: any) => i.id === id)?.display_name) || id,
    [data]
  )

  const getSeverity = useCallback((severity: any) => {
    if (!severity || severity?.value === null) return ''

    return (
      <>
        {`(severity:${severity.value}${severity.max_value ? `/${severity.max_value}` : ''})`}
        <Tooltip
          data-testid='severity-testId'
          title={severity.question_text || 'Details not available'}
          placement="bottom"
        >
          <IconButton size='small' className={classes.infoIcon} sx={{ padding: '0px' }}>
            <InfoIcon className={classes.infoIconFont} />
          </IconButton>
        </Tooltip>
      </>
    )
  }, [classes])

  const getDiagnosisRes = useCallback((d: any) => {
    // Show Yes, No, or scale values 1,2,4,5 (not 3) - excludes Unknown
    const response = d.first_response
    const isScale = d.first_component_type === 'scale'
    const scaleValue = isScale ? parseFloat(response) : null

    // Show (Yes), (No) or scale values (1), (2), (4), (5) - not (3) or (Unknown)
    const showValue = response === 'Yes' || response === 'No' ||
      (isScale && scaleValue !== null && [1, 2, 4, 5].includes(scaleValue))

    return showValue ? `(${response})` : ''
  }, [])

  // Accordion handlers
  const handleAccordion = useCallback((e: any, key: string) => {
    setOpenAccordion(prev => {
      const newSet = new Set(prev)
      const isOpen = newSet.has(key)

      if (isOpen) {
        if (e) newSet.delete(key)
        return newSet
      }

      newSet.add(key)

      setQueObj(prevQueObj => {
        if (!prevQueObj.has(key)) {
          const newMap = new Map(prevQueObj)
          const questionnaireData = data.find((d: any) => d.id === key)
          newMap.set(key, {
            id: key,
            questionnaire_id: questionnaireData?.questionnaire_id,
            confirmed: true,
            answer: {}
          })
          return newMap
        }
        return prevQueObj
      })

      return newSet
    })
  }, [data])

  const closeCurrQuestionnaire = useCallback((id: string) => {
    setOpenAccordion(prev => {
      const newSet = new Set(prev)
      newSet.delete(id)
      return newSet
    })
  }, [])

  // Handle answer changes — uses dataRef to avoid dependency on data state
  const handleChange = useCallback((qId: string, currData: any, initAnswer: boolean) => {
    if (!initAnswer) {
      // User actually changed a value — mark as having unsaved local changes.
      // Normalize to String: qId arrives as a number from QuestionnaireComponent,
      // but handleBulkConfirm looks items up via String(item.id).
      pendingLocalEditsRef.current.add(String(qId))
      // Sync first_response in data so QuickAnswerButtons and the title label stay in sync
      const questionnaireData = dataRef.current.find((d: any) => d.id === qId)
      const firstQuestionId = questionnaireData?.first_question_id
      const firstValue = firstQuestionId != null
        ? currData[firstQuestionId]?.value
        : (Object.values(currData)[0] as any)?.value
      if (firstValue != null) {
        setData((prev: any) => prev.map((d: any) =>
          d.id === qId ? { ...d, first_response: firstValue } : d
        ))
      }
    }
    setQueObj(prev => {
      const newMap = new Map(prev)
      const existingItem = prev.get(qId)
      const questionnaireData = dataRef.current.find((d: any) => d.id === qId)
      // If this is an init answer from a fetch, merge any QuickEdit override so it isn't lost.
      // But skip entirely if the user has already made local edits — a late-arriving init
      // response (async fetch) must not overwrite the user's changed value (e.g. scale 5→4).
      if (initAnswer && pendingLocalEditsRef.current.has(String(qId))) {
        return prev
      }
      let answer = currData
      if (initAnswer) {
        const override = quickEditOverrides.current.get(qId)
        if (override) {
          answer = { ...currData, ...override }
        }
      }
      newMap.set(qId, {
        id: qId,
        questionnaire_id: existingItem?.questionnaire_id || questionnaireData?.questionnaire_id,
        confirmed: true,
        answer
      })
      return newMap
    })
  }, [])

  // Update data helper
  const updateDataItem = useCallback((prevData: any[], itemId: string, updates: any) => {
    return prevData.map((el: any) =>
      el.id === itemId ? { ...el, ...updates, mode: 'view' } : el
    )
  }, [])

  // Handle save operations
  const handleSave = useCallback((type: string, res: any) => {
    if (!res?.id) return

    setIsCurrQuestionLoading(res.id)

    const updates = type === 'confirm'
      ? {
        ...res,
        needs_confirmation: false,
        is_overridden: false,
        overridden_programs: [],
      }
      : res

    setData((prev: any) => updateDataItem(prev, res.id, updates))

    setIsSubmit(false)
    setIsCurrQuestionLoading('')
  }, [updateDataItem])

  // Handle quick answer saves - updates local data immediately (when not using localOnly mode)
  const handleQuickAnswerSaved = useCallback((questionnaireId: string, res: any) => {
    // Update local data to reflect the saved answer
    setData((prev: any) => prev.map((d: any) =>
      d.id === questionnaireId
        ? {
          ...d,
          first_response: res.first_response ?? d.first_response,
          needs_confirmation: false,
          is_overridden: false,
          overridden_programs: []
        }
        : d
    ))

    // Close accordion if it was open
    setOpenAccordion(prev => {
      const next = new Set(prev)
      next.delete(questionnaireId)
      return next
    })

    // Trigger data refresh
    updatePatientData({ isMeasurement: true })
  }, [updatePatientData])

  // Handle quick answer changes in local-only mode (for batching)
  // This stores the answer locally and lets the bulk save mechanism handle it — uses dataRef
  const handleQuickAnswerChanged = useCallback((questionnaireId: string, questionId: string, value: string | number) => {
    // Mark as user-edited so handleBulkConfirm includes this item in validQueObj
    pendingLocalEditsRef.current.add(String(questionnaireId))

    // Update local display immediately
    setData((prev: any) => prev.map((d: any) =>
      d.id === questionnaireId
        ? { ...d, first_response: value }
        : d
    ))

    // Store QuickEdit override in ref (immune to handleChange overwrites)
    const override = { [questionId]: { value } }
    quickEditOverrides.current.set(questionnaireId, override)

    // Store in queObj for bulk save
    setQueObj(prev => {
      const newMap = new Map(prev)
      const questionnaireData = dataRef.current.find((d: any) => d.id === questionnaireId)
      newMap.set(questionnaireId, {
        id: questionnaireId,
        questionnaire_id: questionnaireData?.questionnaire_id,
        confirmed: true,
        answer: { [questionId]: { value } }
      })
      return newMap
    })
  }, [])

  const isRecommended = (questionnaire_id: string) => {
    return (recommended_calculators)?.find((item: any) => item.questionnaire_id === questionnaire_id) ? true : false
  }

  // Bulk confirm/save handler
  const handleBulkConfirm = useCallback(async (type: string) => {
    setIsBulkSaving(true)
    setIsLoading(true)
    // Capture edited item IDs before clearing — used below to exclude items that were only
    // loaded by initAnswer (accordion auto-open) and never touched by the user.
    const userEditedIds = new Set(pendingLocalEditsRef.current)
    pendingLocalEditsRef.current.clear()

    const queObjArray = Array.from(queObj.values())
    // Only include items the user explicitly changed. initAnswer-loaded items (accordion
    // auto-open with backend data) must not be re-saved — they produce stale payloads.
    const validQueObj = queObjArray.filter(item => {
      const hasAnswer = Object.keys(item?.answer || {}).length > 0
      const wasEdited = userEditedIds.has(String(item?.id))
      return hasAnswer && wasEdited
    })

    if (validQueObj.length === 0) {
      // No local edits — submit existing data so the backend can confirm/save without changes
      data.filter((d: any) => d.id).forEach((d: any) => {
        validQueObj.push({
          id: d.id,
          questionnaire_id: d.questionnaire_id,
          confirmed: true,
          answer: {}
        })
      })

      if (validQueObj.length === 0) {
        setIsLoading(false)
        setIsBulkSaving(false)
        handleClose([])
        return
      }
    } else if (type === 'confirm') {
      // Save & Confirm must also confirm the dialog items the user did NOT edit —
      // otherwise their needs_confirmation stays true and the cards remain yellow.
      // Edited items keep their real answers; untouched ones are confirm-only.
      const editedIds = new Set(validQueObj.map(i => String(i.id)))
      data.filter((d: any) => d.id && !editedIds.has(String(d.id))).forEach((d: any) => {
        validQueObj.push({
          id: d.id,
          questionnaire_id: d.questionnaire_id,
          confirmed: true,
          answer: {}
        })
      })
    }

    const itemsToSave = type !== 'confirm'
      ? validQueObj.map(i => ({ ...i, confirmed: false }))
      : validQueObj

    // Assemble the batch-save payload: chip-vs-full-edit routing + pertinent-negative
    // auto-deny + the F122 pertinent-positive guard. Extracted to a pure helper
    // (./category-quesionnaries.batchsave) so the rules are unit-tested in isolation —
    // see category-quesionnaries.batchsave.test.ts. Expected-behavior contract: behaviors
    // B19–B23 in disease_mgmt/docs/technical/PROVIDER_QUESTIONNAIRE_UX_SPEC.md
    // (Questionnaire Entry — Behavior & Test Compendium).
    const { batchByGroup, standardItems } = buildBatchSavePayload({ itemsToSave, data, batchGroupData })

    const saveErrors: string[] = []
    let updatedData = [...data]

    const processResponse = (currItem: any, updatedItem: any) => {
      updatedData = updatedData.map((el: any) =>
        el.id === currItem.id
          ? {
            ...el,
            needs_confirmation: updatedItem.needs_confirmation,
            needs_update: updatedItem.needs_update,
            is_overridden: updatedItem.is_overridden,
            overridden_programs: updatedItem.overridden_programs,
            severity: updatedItem.severity || null,
            first_response: updatedItem.first_response,
            created_on: updatedItem.created_on,
            mode: 'view'
          }
          : el
      )
      handleChange(currItem.id, updatedItem.questionnaire?.answer_set, false)
    }

    try {
      // Save standard items via updateQuestionnaires
      if (standardItems.length > 0) {
        const batchPayload = {
          data: standardItems.map(currItem => ({
            ...currItem,
            is_recommended: isRecommended(currItem.questionnaire_id)
          }))
        }

        try {
          const res = await patientService.updateQuestionnaires(patientDetail.id, batchPayload)
          const results = res?.data?.result || []
          standardItems.forEach((currItem: any, idx: number) => {
            if (results[idx]) {
              processResponse(currItem, results[idx])
            } else {
              saveErrors.push(getQuestionnaireName(currItem.id))
            }
          })
        } catch (err: any) {
          const results = err?.response?.data?.result || []
          if (results.length > 0) {
            standardItems.forEach((currItem: any, idx: number) => {
              if (results[idx]) {
                processResponse(currItem, results[idx])
              } else {
                saveErrors.push(getQuestionnaireName(currItem.id))
              }
            })
            const msg = err?.response?.data?.message || ''
            if (msg.includes('not confirmed')) {
              toast.warning('Saved but some items have incomplete responses — not confirmed.', Helper.bottom_center())
            }
          } else {
            console.error('Standard save failed:', err?.response?.data || err)
            standardItems.forEach((currItem: any) => saveErrors.push(getQuestionnaireName(currItem.id)))
          }
        }
      }

      // Merge all batch groups into a single API call — multiple calls cause
      // set_recommended_diagnosis_internal to stomp on earlier groups' results.
      const batchEntries = Object.entries(batchByGroup).filter(([, q]) => q.length > 0)
      if (batchEntries.length > 0) {
        const allBatchItems = batchEntries.flatMap(([, questionnaires]) => questionnaires)
        const allBatchGroups = batchEntries.map(([bg]) => bg)
        try {
          await patientService.batchSaveQuestionnaires(
            patientDetail.id, allBatchGroups.join(','), allBatchItems
          )
          // Reflect batch-saved values in updatedData so the parent's catData is up-to-date
          // when the dialog closes and reopens. The batch endpoint updates questionnaire
          // records, not measurements.calculators, so updatePatientData alone won't refresh
          // the chip state in the calculator dialog.
          allBatchItems.forEach(({ name, value }) => {
            const idx = updatedData.findIndex((d: any) => d.name === name)
            if (idx >= 0) {
              updatedData[idx] = { ...updatedData[idx], first_response: value, needs_confirmation: false }
            }
          })
        } catch (err: any) {
          console.error('Batch save failed:', err?.response?.data || err)
          saveErrors.push('batch_save')
        }
      }

      // B26 live-refresh: branched (inline-edited) chips re-color from the ROOT of the edited
      // answer set (authoritative), via the unit-pinned helper. Parity with search S1; complements
      // processResponse; no-ops when the chip has no first_question_id.
      const _branchedRefresh = computeBranchedRefresh(
        batchEditAnswersRef.current,
        (id: string) => updatedData.find((d: any) => String(d.id) === String(id)),
      )
      Object.entries(_branchedRefresh).forEach(([id, val]) => {
        const idx = updatedData.findIndex((d: any) => String(d.id) === String(id))
        if (idx >= 0) updatedData[idx] = { ...updatedData[idx], first_response: val, needs_confirmation: false }
      })

      setData(updatedData)

      // Clear overrides after save — the backend now has the real data
      quickEditOverrides.current.clear()
      batchEditAnswersRef.current.clear()
      // Collapse any open inline questionnaire on Save — the committed answer is now
      // reflected on the chip; leaving the editor open is disorienting on a long screen.
      setEditingBatchItem(null)

      if (saveErrors.length > 0) {
        toast.error(`Failed to update ${saveErrors.join(', ')}.`, Helper.bottom_center())
      }
      onDataSaved?.(updatedData)
      if (type === 'confirm') {
        // Brief pause for backend auto-complete propagation (dx_questionnaire_mapping)
        await new Promise(resolve => setTimeout(resolve, 500))
        await updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        handleClose(updatedData)
      } else {
        // Save only — keep dialog open so provider can continue editing
        toast.success('Saved successfully', Helper.bottom_center())
      }
    } catch (error) {
      console.error('Error in bulk confirm:', error)
      toast.error('An error occurred while saving questionnaires.', Helper.bottom_center())
    } finally {
      setIsLoading(false)
      setIsBulkSaving(false)
      setIsSubmit(false)
    }
  }, [queObj, data, patientDetail.id, patientService, batchGroupData, getQuestionnaireName, handleChange, updatePatientData, handleClose, onDataSaved])

  const handleCatDialogClose = useCallback(() => {
    //  handleClose(data)
    handleClose([])
    updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
  }, [data, handleClose, updatePatientData])

  const handleIsSubmit = useCallback((flag: boolean) => {
    setIsSubmit(flag)
  }, [])

  // Memoized questionnaire items filtering
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return []

    // Search mode surfaces the full eval-union catalog program-agnostically (F118 R2/R3):
    // the whole point is to find questionnaires NOT in the patient's enrolled programs —
    // e.g. a homeless *_unspecified questionnaire whose only "program" is the hidden
    // `unspecified` catch-all. Program-filtering here would drop exactly those and the
    // clicked item would render nothing ("nothing opens").
    if (input?.dependent_questionnaires || isSearch || isBulkEdit || selectedProgramSet.size === 0) return data
    return data.filter((d: any) => d.programs?.some((prog: string) => selectedProgramSet.has(prog)))
  }, [data, selectedProgramSet, isBulkEdit, isSearch, input?.dependent_questionnaires])


  // Batch chip toggle handler
  const handleBatchChipToggle = useCallback((item: any, isSelected: boolean) => {
    if (!isSelected) {
      const denyValue = chipDenyValue(item)
      const questionId = item.first_question_id || item.name
      // Clear stale editor state
      quickEditOverrides.current.delete(item.id)
      batchEditAnswersRef.current.delete(item.id)
      // Deselect IS a pending change — store the deny value so handleBulkConfirm sends it correctly.
      // Deleting from queObj caused the fallback to use first_response=null → value='5' (wrong).
      pendingLocalEditsRef.current.add(String(item.id))
      setQueObj(prev => {
        const newMap = new Map(prev)
        newMap.set(item.id, {
          id: item.id,
          questionnaire_id: item.questionnaire_id,
          confirmed: true,
          answer: { [questionId]: { value: denyValue } }
        })
        return newMap
      })
      setData((prev: any) => prev.map((d: any) =>
        d.id === item.id ? { ...d, first_response: denyValue } : d
      ))
      return
    }
    // B8/B9 convergence (S2): confirm default shared with S1 via chipConfirmDefault. NOTE: S2
    // does NOT yet preserve a real saved value here (B9) — BatchChipGroup's selected-state keys
    // off first_response ∈ {Yes,5,4}, so a preserved '3' would render deselected. Tracked gap.
    const value = chipConfirmDefault(item)
    pendingLocalEditsRef.current.add(String(item.id))  // chip selection is an unsaved local change
    // Update local display
    setData((prev: any) => prev.map((d: any) =>
      d.id === item.id ? { ...d, first_response: value } : d
    ))
    // Use the actual question ID from the backend (derived from questionnaire definition),
    // not a fragile suffix-strip of the calculator name.
    const questionId = item.first_question_id || item.name
    quickEditOverrides.current.set(item.id, { [questionId]: { value } })
    setQueObj(prev => {
      const newMap = new Map(prev)
      newMap.set(item.id, {
        id: item.id,
        questionnaire_id: item.questionnaire_id,
        confirmed: true,
        answer: { [questionId]: { value } }
      })
      return newMap
    })
  }, [])

  // Batch deny-all handler — only deny recommended items.
  // Non-recommended items default to Unknown and shouldn't be saved
  // unless the user explicitly clicks them.
  // Defense-in-depth guard: exposure-dependent batch groups should not
  // cascade-deny. BatchChipGroup hides its "No" chip for these categories
  // so onDenyAll is never called from the UI — this check protects against
  // any non-UI caller (keyboard shortcut, future code path) reaching the
  // cascade with an exposure-dependent batch. Keep in sync with
  // EXPOSURE_DEPENDENT_CATEGORIES in BatchChipGroup.tsx. See SOLUTION_LOG
  // 2026-05-24 "CAPA 1 close" for the harm pathway.
  const EXPOSURE_DEPENDENT_CATEGORIES_GUARD = new Set(['ALLEVIATING', 'AGGRAVATING'])
  const handleBatchDenyAll = useCallback((items: any[]) => {
    const recommendedItems = items.filter(item => item.is_recommended !== false)
    if (recommendedItems.length === 0) return
    if (EXPOSURE_DEPENDENT_CATEGORIES_GUARD.has(recommendedItems[0]?.ui_category)) return

    // Build all updates in one pass
    const dataUpdates = new Map<string, string>()
    const queObjUpdates = new Map<string, any>()

    recommendedItems.forEach(item => {
      const value = chipDenyValue(item)
      const questionId = item.first_question_id || item.name

      dataUpdates.set(item.id, value)
      quickEditOverrides.current.set(item.id, { [questionId]: { value } })
      queObjUpdates.set(item.id, {
        id: item.id,
        questionnaire_id: item.questionnaire_id,
        confirmed: true,
        answer: { [questionId]: { value } }
      })
    })

    // Single setData call
    setData((prev: any) => prev.map((d: any) => {
      const newValue = dataUpdates.get(d.id)
      return newValue !== undefined ? { ...d, first_response: newValue } : d
    }))

    // Single setQueObj call
    setQueObj(prev => {
      const newMap = new Map(prev)
      queObjUpdates.forEach((val, key) => newMap.set(key, val))
      return newMap
    })
  }, [])

  // Handle inline editing of a batch chip item
  const handleBatchChipEdit = useCallback((item: any) => {
    // Toggle: if already editing this item, close it
    if (editingBatchItem?.name === item.name) {
      setEditingBatchItem(null)
      return
    }
    setEditingBatchItem(item)
  }, [editingBatchItem])

  // Handle answer change from inline batch editor
  const handleBatchEditChange = useCallback((qId: string, currData: any, initAnswer: boolean) => {
    const item = editingBatchItem
    // Persist user-edited answers so they survive chip navigation (unmount/remount)
    if (!initAnswer && item) {
      batchEditAnswersRef.current.set(item.id, currData)
      // Mark as user-edited so handleBulkConfirm doesn't treat this as an initAnswer-only entry
      pendingLocalEditsRef.current.add(String(item.id))
    }
    // A late-arriving initAnswer=true (async fetch response) must not overwrite a value
    // the user has already edited — e.g. scale changed from 5 to 4.
    if (initAnswer && item && batchEditAnswersRef.current.has(item.id)) {
      return
    }
    // Store in queObj for bulk save
    setQueObj(prev => {
      const newMap = new Map(prev)
      newMap.set(item?.id || qId, {
        id: item?.id || qId,
        questionnaire_id: item?.questionnaire_id,
        confirmed: true,
        answer: currData
      })
      return newMap
    })
    // Intentionally not calling setData here — updating first_response during editing
    // would trigger a filteredData → renderGroup → BatchChipGroup responseKey cascade
    // that deselects chips whose value falls below the selection threshold (e.g. "No", 1-3),
    // causing the chip to behave as unselected while the editor is open. first_response
    // is refreshed correctly after the outer Save via handleBulkConfirm.
  }, [editingBatchItem])

  // Handle save from inline batch editor
  const handleBatchEditSave = useCallback((type: string, res: any) => {
    if (!res?.id) return
    setData((prev: any) => prev.map((d: any) =>
      d.id === res.id ? { ...d, ...res, mode: 'view' } : d
    ))
    // Sync queObj with the actual saved value so bulk Save & Confirm sends the correct value
    if (res.first_response != null) {
      setQueObj(prev => {
        const existing = prev.get(res.id)
        if (!existing) return prev
        const answerKeys = Object.keys(existing.answer || {})
        if (answerKeys.length === 0) return prev
        const next = new Map(prev)
        next.set(res.id, {
          ...existing,
          answer: { [answerKeys[0]]: { value: String(res.first_response) } }
        })
        return next
      })
    }
    // Close the inline editor after successful save
    setEditingBatchItem(null)
  }, [])

  const recalculateData = () => {
    updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
  }

  // Batch layout (batch chip groups + individual items). Extracted from renderGroup so the
  // "No to all remaining" handler can read the same batchGroupItems the renderer draws.
  const { batchGroupItems, individualItems, sortedData } = useMemo(() => {
    if (filteredData.length === 0) {
      return { batchGroupItems: [] as { bg: string, items: any[] }[], individualItems: [] as any[], sortedData: [] as any[] }
    }

    // Sort by ORIGINAL display_name (with prefix) to preserve grouping
    const sortedData = [...filteredData].sort((a: any, b: any) =>
      (a.display_name || '').toLowerCase().localeCompare((b.display_name || '').toLowerCase())
    )

    // Separate batch-grouped items from individual items
    const batchMapping = batchGroupData.mapping || {}
    const batchGroups = batchGroupData.groups || {}
    const activeBatchGroups = new Set<string>()
    const resultsByName: { [name: string]: any } = {}

    // First pass: discover which batch groups are active based on batchMapping
    sortedData.forEach((d: any) => {
      resultsByName[d.name] = d
      const bgInfo = batchMapping[d.name]
      if (bgInfo?.batch_group) {
        activeBatchGroups.add(bgInfo.batch_group)
      }
    })

    // Build batch group items, collecting all names covered by any active group
    const coveredByBatchGroup = new Set<string>()
    const batchGroupItems: { bg: string, items: any[] }[] = []
    activeBatchGroups.forEach(bg => {
      const groupInfo = batchGroups[bg]
      if (!groupInfo?.items) return
      const items = groupInfo.items.map((item: any) => {
        coveredByBatchGroup.add(item.name)
        const existing = resultsByName[item.name]
        return {
          id: existing?.id || item.name,
          questionnaire_id: existing?.questionnaire_id || 0,
          name: item.name,
          display_name: existing?.display_name || item.name,
          option_label: item.option_label,
          first_response: existing?.first_response || null,
          first_component_type: existing?.first_component_type || item.first_component_type || 'scale',
          first_question_id: existing?.first_question_id || null,
          is_recommended: !!existing,
          // Stamp bg-level ui_category onto every item so the cascade-No
          // defense-in-depth guard in handleBatchDenyAll can read it.
          ui_category: groupInfo?.ui_category,
        }
      })
      batchGroupItems.push({ bg, items })
    })

    // Individual items = anything not covered by a batch group (handles incomplete mapping)
    const individualItems: any[] = sortedData.filter((d: any) => !coveredByBatchGroup.has(d.name))

    return { batchGroupItems, individualItems, sortedData }
  }, [filteredData, batchGroupData])

  // "No to all remaining": apply the existing per-group deny to every batch group that has no
  // positive answer yet (selectGroupsToDeny encodes the rule + exposure-dependent guard).
  // Reuses handleBatchDenyAll so deny value resolution and the F122 guard stay in one place.
  const handleDenyAllRemaining = useCallback(() => {
    selectGroupsToDeny(batchGroupItems).forEach(({ items }) => handleBatchDenyAll(items))
  }, [batchGroupItems, handleBatchDenyAll])

  // Whether any batch group still qualifies for the bulk deny (controls button enablement).
  const hasRemainingToDeny = useMemo(
    () => selectGroupsToDeny(batchGroupItems).length > 0,
    [batchGroupItems]
  )

  // Render questionnaire group
  const renderGroup = useMemo(() => {
    if (filteredData.length === 0) return null

    const batchGroups = batchGroupData.groups || {}

    const qData = individualItems.map((d: any, i: number) => {
      // Check if we need a separator line (prefix changed from previous item)
      const prevItem = i > 0 ? sortedData[i - 1] : null
      const showSeparator = prevItem && hasDifferentPrefix(prevItem.display_name, d.display_name)
      const isExpanded = openAccordion.has(d.id)
      const isLoading = isCurrQuestionLoading === d.id

      // Get options: prefer backend first_options, fall back to lazy-loaded ref
      const options = d.first_options || questionnaireOptionsRef.current.get(d.id)

      return (
        <React.Fragment key={`calculator-${title}-${d.id || d.name || d.questionnaire_id || i}`}>
          {/* Light separator line when prefix changes */}
          {showSeparator && (
            <Divider sx={{ my: 0.25, borderColor: 'rgba(0, 0, 0, 0.08)', borderBottomWidth: '0.5px' }} />
          )}
          <Accordion
            square
            disableGutters
            expanded={isExpanded}
            className={classes.patientAccrodionBg}
            sx={{ '& .MuiSvgIcon-root': { fontSize: '0.75rem !important' } }}
          >
            <AccordionSummary
              className={classes.zeroPadding}
              ref={(el) => queRefs.current[i] = el}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel-content-${title}-${d.id}`}
              id={`panel-header-${title}-${d.id}`}
              onClick={(e) => handleAccordion(e, d.id)}
              sx={{
                minHeight: 'auto !important',
                '&.Mui-expanded': { minHeight: 'auto !important' },
                '& .MuiAccordionSummary-content': { margin: '4px 0 !important' },
                '& .MuiAccordionSummary-content.Mui-expanded': { margin: '4px 0 !important' },
              }}
            >
              <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                <Box sx={{ flex: 1 }}>
                  {isLoading ? (
                    <CircularProgress size="2rem" />
                  ) : (
                    <CardLayoutComponent
                      id={d.id}
                      name={d.name}
                      input={{ cond_per_assessments: d.cond_per_assessments }}
                      leftHeader={input?.isDependentInfo ? Object.values(d)[0] : formatDisplayName(d.display_name) || ''}
                      rightHeader={''}
                      subHeader={
                        title?.includes('Clinical assessments')
                          ? getDiagnosisRes(d)
                          : getSeverity(d.severity)
                      }
                      bottomText={''}
                      needs_confirmation={d.needs_confirmation ?? undefined}
                      needs_update={d.needs_update || undefined}
                      icons={[]}
                      programs={d.programs}
                      overridenPrograms={d.overridden_programs}
                      onRefClick={() => null}
                      onOverrideClick={() => { }}
                      is_referred={d.is_referred}
                      is_overridden={d.is_overridden || undefined}
                      entityRef={'calculator'}
                      patientId={patientDetail.id}
                      category={title === 'diagnosis' ? 'conditions' : 'measurements'}
                      showDiagnosticIndicator={true}
                    />
                  )}
                </Box>
                {!isLoading && !input?.isDependentInfo && (
                  <QuickAnswerButtons
                    questionnaire={{
                      id: d.id,
                      questionnaire_id: d.questionnaire_id,
                      first_component_type: d.first_component_type,
                      first_response: d.first_response,
                      needs_confirmation: d.needs_confirmation,
                      recent_confirmation: d.recent_confirmation,
                      options: options,
                      first_question_id: d.first_question_id || null
                    }}
                    patientId={patientDetail.id}
                    isRecommended={isRecommended(d.questionnaire_id)}
                    onAnswerSaved={handleQuickAnswerSaved}
                    onAnswerChanged={handleQuickAnswerChanged}
                    disabled={false}
                    compact={input?.dependent_questionnaires ? true : false}
                    localOnly={true}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {isExpanded && (
                <Stack justifyContent="center" className={classes.zeroTopPadding}>
                  <QuestionnaireComponent
                    data={{ isBulkEdit: true }}
                    pId={patientDetail.id}
                    qId={d.questionnaire_id}
                    qu={d.id}
                    is_recommended={isRecommended(d.questionnaire_id)}
                    isAllQuestionnarie={true}
                    handleClose={() => closeCurrQuestionnaire(d.id)}
                    isSubmit={isSubmit}
                    handleChange={handleChange}
                    handleIsSubmit={handleIsSubmit}
                    handleSave={handleSave}
                    needsConfirmation={d.needs_confirmation || null}
                    presetIntent={(() => {
                      // Backend previews only when nothing is saved, so passing this from the
                      // clicked value is safe; shows propagated defaults on open (INV-1).
                      const v = String(d.first_response ?? '')
                      if (['Yes', '5', '4'].includes(v)) return 'confirm'
                      if (['No', '1', '2'].includes(v)) return 'deny'
                      return undefined
                    })()}
                    overrideAnswer={quickEditOverrides.current.get(d.id)}
                    // Callback to receive options — stored in ref to avoid re-render cascade
                    onOptionsLoaded={(opts: string[]) => {
                      questionnaireOptionsRef.current.set(d.id, opts)
                    }}
                  />
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </React.Fragment>
      )
    })

    return <Stack direction={'column'} spacing={0.5}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: input?.dependent_questionnaires
          ? '-0.5rem !important'
          : '0.3rem !important'
      }}
      >&nbsp;
        {/* {(input?.dependent_questionnaires)
        // !((isSearch && item?.length === 1) && !input?.dependent_questionnaires)
          ? 
          <FormControlLabel
            onClick={(e) => e.stopPropagation()}
            control={
              <Checkbox
                size="small"
                sx={{
                  padding: '0rem !important',
                  transform: input?.dependent_questionnaires ? "scale(1) !important" : "scale(0.7) !important",
                }}
                checked={isExpandQ}
                onChange={(e: any) => setIsExpandQ(!isExpandQ)}
              />
            }
            label={<Typography pl={input?.dependent_questionnaires ? 0.5 : 0} className={classes.desc} variant="body2">
              {`${'Expand/Collapse'} all`}
            </Typography>
            }
            aria-setsize={10}
          />
          : ''
        } */}
      </Box>

      {/* Batch chip groups — inline editor renders right below the group containing the editing chip */}
      {batchGroupItems.map(({ bg, items }) => {
        const groupInfo = batchGroups[bg]
        const groupLabel = groupInfo?.display_label || bg.replace(/_/g, ' ')
        const uiCategory = groupInfo?.ui_category
        const isEditingInThisGroup = editingBatchItem
          ? items.some(i => i.name === editingBatchItem.name)
          : false
        return (
          <React.Fragment key={bg}>
            <BatchChipGroup
              batchGroup={bg}
              groupLabel={groupLabel}
              items={items}
              onChipToggle={handleBatchChipToggle}
              onDenyAll={handleBatchDenyAll}
              onChipEdit={handleBatchChipEdit}
              editingItemName={editingBatchItem?.name}
              uiCategory={uiCategory}
            />
            <Collapse in={isEditingInThisGroup} unmountOnExit>
              {isEditingInThisGroup && editingBatchItem && editingBatchItem.questionnaire_id > 0 && (
                <Card variant="outlined" sx={{
                  mt: 0.5, mb: 1,
                  backgroundColor: 'var(--color-eval-card-bg) !important',
                  borderColor: 'warning.main',
                  borderWidth: '1px',
                }}>
                  <CardContent sx={{ padding: '0.25rem 0.5rem !important' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        {editingBatchItem.option_label || formatDisplayName(editingBatchItem.display_name || '')}
                      </Typography>
                      <IconButton size="small" onClick={() => setEditingBatchItem(null)} sx={{ padding: '2px' }}>
                        <CloseIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Stack>
                    <QuestionnaireComponent
                      key={editingBatchItem.id}
                      data={{ isBulkEdit: true, isSearch:true }}
                      pId={patientDetail.id}
                      qId={String(editingBatchItem.questionnaire_id)}
                      qu={editingBatchItem.id}
                      is_recommended={editingBatchItem.is_recommended !== false}
                      isAllQuestionnarie={false}
                      handleClose={() => setEditingBatchItem(null)}
                      isSubmit={isSubmit}
                      handleChange={handleBatchEditChange}
                      handleIsSubmit={handleIsSubmit}
                      handleSave={handleBatchEditSave}
                      needsConfirmation={null}
                      presetIntent={(() => {
                        const v = String(editingBatchItem.first_response ?? '')
                        if (['Yes', '5', '4'].includes(v)) return 'confirm'
                        if (['No', '1', '2'].includes(v)) return 'deny'
                        return undefined
                      })()}
                      overrideAnswer={batchEditAnswersRef.current.get(editingBatchItem.id)}
                    />
                  </CardContent>
                </Card>
              )}
            </Collapse>
          </React.Fragment>
        )
      })}

      {/* Individual items */}
      <Box>{qData}</Box>
    </Stack>
  }, [filteredData, batchGroupData, openAccordion, isCurrQuestionLoading, title, classes, handleAccordion, handleBatchChipToggle, handleBatchDenyAll, handleBatchChipEdit, editingBatchItem, handleBatchEditChange, handleBatchEditSave, getDiagnosisRes, getSeverity, patientDetail.id, closeCurrQuestionnaire, isSubmit, handleChange, handleIsSubmit, handleSave])


  // Action buttons
  const actionButtons = useMemo(() => {
    const isProcessing = (isBulkSaving ||
      (input?.dependent_questionnaires && patientDetailStatus === 'loading')
    )

    return (
      <DialogActions
        className={classes.btnContainer}
        sx={{
          backgroundColor: isSearch ? 'var(--color-paper) !important' : '',
          padding: `0.5rem 1.375rem 1rem 0 !important`,
          borderRadius: isSearch ? '4px' : '0'
        }}
      >
        <Button
          disabled={isProcessing}
          onClick={() => handleBulkConfirm('confirm')}
          variant="outlined"
          size='small'
        >
          <strong>{isProcessing ? 'Saving...' : 'Save & Confirm'}</strong>
        </Button>
        <Button
          disabled={isProcessing}
          onClick={() => handleBulkConfirm('save')}
          variant="outlined"
          size='small'
        >
          <strong>{isProcessing ? 'Saving...' : 'Save'}</strong>
        </Button>
        <Button 
          startIcon={<SyncIcon sx={{ fontSize:'0.7rem !important' }}/>}
          disabled={isBulkSaving || patientDetailStatus === 'loading'} 
          onClick={() => recalculateData()} 
          variant="outlined" 
          size="small"
          sx={{ '& .MuiButton-startIcon': { marginRight:'0.25rem' } }}
        >
          <strong>{'Recalculate'}</strong>
        </Button>
        <Button
          onClick={() => handleClose([])}
          variant="outlined"
          size='small'
          disabled={isProcessing}
        >
          <strong>Cancel</strong>
        </Button>
      </DialogActions>
    )
  }, [classes, isSearch, isBulkSaving, handleBulkConfirm, handleCatDialogClose, input?.dependent_questionnaires])

  // Search view
  if (isSearch) {
    return (
      <Stack direction={'column'} spacing={0}>
        {renderGroup}
        {/* Divider to separate content from action buttons */}
        <Divider sx={{ mt: 1.5, mb: 0.5 }} />
        {actionButtons}
      </Stack>
    )
  }

  // Dialog view
  return (
    <Draggable ref={calRef} handle='.category-questionnarie-dialog'>
      <Dialog
        id={'cal-q-id'}
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
          handleCatDialogClose()
        }}
        aria-labelledby="calcluator-questionnarie"
        hideBackdrop
        disableScrollLock
        disableEscapeKeyDown
        onKeyUp={(e) => {
          e.preventDefault()
          if (e.key === 'Enter' && queObj.size >= 1 && !isLoading) {
            handleBulkConfirm('confirm')
          }
        }}
        sx={{ position: "absolute", left: '15%', top: '10%' }}
      >
        <DialogTitle
          id="calcluator-questionnarie-title"
          className={classes.calculatorQuestionnarieTitle}
          classes={{ root: 'category-questionnarie-dialog' }}
        >
          <DialogTitleHeaderComponent title={title} handleDialogClose={handleCatDialogClose} />
        </DialogTitle>

        {/* LinearProgress showing until all questionnaires are loaded */}
        {(isLoading || isBulkSaving || patientDetailStatus === 'loading') && (
          <LinearProgress variant="indeterminate" sx={{ height: '4px', backgroundColor: 'var(--color-body-background)' }} />
        )}
        <DialogContent className={classes.questionnaireContainer}>
          <Stack direction={'column'} spacing={0} className={classes.questionnaireCardContainer} sx={{ minHeight: '200px' }}>
            {!(isLoading || isSubmit || patientDetailStatus === 'loading') && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                <Tooltip title="Marks every group without a positive answer as No">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!hasRemainingToDeny || isBulkSaving}
                      onClick={handleDenyAllRemaining}
                    >
                      <strong>No to all remaining</strong>
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
            {!(isLoading || isSubmit || patientDetailStatus === 'loading') &&
              renderGroup
            }
          </Stack>
        </DialogContent>
        <Box className={classes.boxBorder} />
        {actionButtons}
      </Dialog>
    </Draggable>
  )
}

export default withStyles(stylesheet)(CategoryQuestionnaireComponent)